import Swiper from 'swiper';

import { SWIPER_SELECTORS } from '~/constants/swiper';
import * as DataDogLogs from '~/lib/datadog/datadog';

/**
 * Initializes a Swiper instance with auto-scoped navigation/pagination elements
 *
 * @param {HTMLElement} container - Container element that holds the `.swiper` element and navigation/pagination elements
 * @param {import('swiper/types').SwiperOptions} options - Swiper configuration options
 * @returns {Swiper | null} Swiper instance or null if `.swiper` element not found
 *
 * @example
 * // Typical DOM structure:
 * // <div data-section-type="my-carousel">     ← Pass this as container
 * //   <div class="swiper">
 * //     <div class="swiper-wrapper">...</div>
 * //   </div>
 * //   <button class="swiper-prev-button">
 * //   <button class="swiper-next-button">
 * // </div>
 *
 * const carousel = initSwiper(container, {
 *   modules: [Navigation],
 *   slidesPerView: 1.3,
 *   spaceBetween: 10,
 * });
 */
export function initSwiper(container, options) {
  const swiperElement = container.querySelector(SWIPER_SELECTORS.CONTAINER);

  if (!swiperElement) {
    DataDogLogs.error('[Allbirds utils] - initSwiper - .swiper element not found', {
      containerClasses: container?.className,
      containerExists: !!container,
      containerTag: container?.tagName,
    });
    return null;
  }

  const config = createSwiperConfig(container, options);
  return new Swiper(swiperElement, config);
}

/**
 * Creates a Swiper configuration with scoped navigation/pagination/scrollbar elements
 *
 * @param {HTMLElement} container - Container element holding `.swiper` and control elements
 * @param {import('swiper/types').SwiperOptions} options - Swiper configuration options
 * @returns {import('swiper/types').SwiperOptions} Complete Swiper configuration with scoped controls
 */
function createSwiperConfig(container, options) {
  const config = { ...options };

  // If modules are provided, try to auto-scope control elements
  // Note: Module names get minified in production (e.g., "Navigation" becomes "N"),
  // so we check if modules array exists and look for control elements
  if (options.modules && options.modules.length > 0) {
    // Configure navigation if elements are found or config exists
    const navConfig = options.navigation || {};
    const nextButton = navConfig.nextEl
      ? scopeElement(container, navConfig.nextEl)
      : container.querySelector(SWIPER_SELECTORS.NEXT_BUTTON);
    const prevButton = navConfig.prevEl
      ? scopeElement(container, navConfig.prevEl)
      : container.querySelector(SWIPER_SELECTORS.PREV_BUTTON);

    if (nextButton || prevButton || 'navigation' in options) {
      if (!nextButton || !prevButton) {
        DataDogLogs.warn('[Allbirds utils] - createSwiperConfig - Navigation buttons not found', {
          containerClasses: container?.className,
          nextButton: !!nextButton,
          prevButton: !!prevButton,
        });
      }

      config.navigation = {
        ...navConfig,
        nextEl: nextButton,
        prevEl: prevButton,
      };
    }

    // Configure pagination if element is found or config exists
    const paginationConfig = options.pagination || {};
    const paginationEl = paginationConfig.el
      ? scopeElement(container, paginationConfig.el)
      : container.querySelector(SWIPER_SELECTORS.PAGINATION);

    if (paginationEl || 'pagination' in options) {
      config.pagination = {
        ...paginationConfig,
        el: paginationEl,
      };
    }

    // Configure scrollbar if element is found or config exists
    const scrollbarConfig = options.scrollbar || {};
    const scrollbarEl = scrollbarConfig.el
      ? scopeElement(container, scrollbarConfig.el)
      : container.querySelector(SWIPER_SELECTORS.SCROLLBAR);

    if (scrollbarEl || 'scrollbar' in options) {
      config.scrollbar = {
        ...scrollbarConfig,
        el: scrollbarEl,
      };
    }
  }

  return config;
}

/**
 * Helper to scope a selector to a container element
 * @param {HTMLElement} container - Container to search within
 * @param {string|HTMLElement} selector - Selector string or element reference
 * @returns {HTMLElement|null} Scoped element or null
 */
function scopeElement(container, selector) {
  if (typeof selector === 'string') {
    return container.querySelector(selector);
  }
  return selector;
}
